import React, { useState } from "react";
import { X, Mail, Send } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { INVITE_ROLES } from "./shared";

/**
 * Invite dialog — emails the recipient a one-time accept-link instead of
 * setting a password up-front. Called from Users.jsx with:
 *   <InviteDialog
 *     initial={{ email: "", name: "", role: "VIEWER", photographer_id: "" }}
 *     photogs={[…]}
 *     onClose={…}
 *     onSent={(email) => …}
 *   />
 *
 * ADMIN role is intentionally excluded from the invite flow — admins are
 * promoted from existing accepted users for an audit trail.
 */
export default function InviteDialog({ initial, photogs, onClose, onSent }) {
  const [form, setForm] = useState(initial);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const payload = {
        email: form.email.trim(),
        name: (form.name || "").trim() || undefined,
        role: form.role,
      };
      if (form.role === "MEMBER") {
        if (!form.photographer_id) {
          setErr("Please link this invitation to a photographer.");
          setBusy(false);
          return;
        }
        payload.photographer_id = form.photographer_id;
      }
      await api.post(`/users/invite`, payload);
      onSent(form.email.trim());
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="dialog-bg" onClick={onClose}>
      <form className="dialog-panel p-6" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-xl font-semibold flex items-center gap-2">
            <Mail size={18} className="text-amber-400" /> Invite a teammate
          </h3>
          <button type="button" onClick={onClose}><X size={18} /></button>
        </div>

        <p className="muted-text text-sm mb-5">
          We&rsquo;ll email them a one-time link to set their own password — no
          temporary passwords to manage. The link is valid for 7 days.
        </p>

        <div className="form-row">
          <label className="ccs-label">Email *</label>
          <input
            required type="email" autoFocus className="ccs-input"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="teammate@example.com"
            data-testid="invite-email-input"
          />
        </div>

        <div className="form-row">
          <label className="ccs-label">Name (optional)</label>
          <input
            className="ccs-input"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="They can change this themselves on accept"
            data-testid="invite-name-input"
          />
        </div>

        <div className="form-row">
          <label className="ccs-label">Role *</label>
          <select
            className="ccs-input" value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value })}
            data-testid="invite-role-select"
          >
            {INVITE_ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          <div className="muted-text text-xs mt-1">
            Need another ADMIN? Create them as SECRETARY first, then promote
            them once they&rsquo;ve accepted — keeps an audit trail.
          </div>
        </div>

        {form.role === "MEMBER" && (
          <div className="form-row">
            <label className="ccs-label">Linked photographer *</label>
            <select
              required className="ccs-input"
              value={form.photographer_id || ""}
              onChange={(e) => setForm({ ...form, photographer_id: e.target.value })}
              data-testid="invite-photographer-select"
            >
              <option value="">Select photographer…</option>
              {photogs.map((p) => (
                <option key={p.id} value={p.id}>{p.photographer_code} — {p.display_name}</option>
              ))}
            </select>
          </div>
        )}

        {err && <div className="danger-text text-sm" data-testid="invite-dialog-error">{err}</div>}

        <div className="flex justify-end gap-3 mt-5">
          <button type="button" onClick={onClose} className="ccs-btn-ghost">Cancel</button>
          <button type="submit" disabled={busy || !form.email} className="ccs-btn flex items-center gap-2" data-testid="invite-send">
            <Send size={14} /> {busy ? "Sending…" : "Send invitation"}
          </button>
        </div>
      </form>
    </div>
  );
}
